import {useAddComment, CommentsCategory} from '@domain/comments'
import {usePlantConfig} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  ProcessWorkOrderDetails,
  ProcessWorkOrderTaskCard,
  type ProcessWorkOrderTaskCardItem
} from '../../components'
import {FEATURE_NAMESPACE} from '../../consts'
import {useProcessWorkOrder, useUpdateProcessWorkOrder} from '../../hooks'
import {mapToSingleProcessWorkOrderStatusUpdateDto} from '../../mappers'
import {
  HProduceTaskIdentifiers,
  HProduceTask,
  ProcessWorkOrderStatus,
  SideCardItemMode,
  ProcessWorkOrderDto
} from '../../types'
import {
  ProcessWorkOrderCommentsContainer,
  ProcessWorkOrderCommentsQueryKeyPrefix
} from '../ProcessWorkOrderCommentsContainer'

import {ShiftTaskAttachmentsContainer} from './ShiftTaskAttachmentsContainer'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onEditTaskClick: (taskId: string, mode: SideCardItemMode) => void
}

export const ProcessWorkOrderContainer = ({task, onClose, onEditTaskClick}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  // Fetch process work order data from the service using the GET endpoint based on HproduceTask id
  const {data, isLoading, isError, refetch, isRefetching} = useProcessWorkOrder(task)
  // Process work orders use Plant timezone
  const {data: configData, isLoading: isLoadingConfig} = usePlantConfig({
    plantId: task.plantId,
    language: i18n.language
  })
  const [isLoadingEditingDeleting, setIsLoadingEditingDeleting] = useState(false)
  const {mutate: updateProcessWorkOrder} = useUpdateProcessWorkOrder()
  const {mutateAsync: addComment} = useAddComment(
    {
      plantId: task.plantId,
      commentsCategory: CommentsCategory.ProcessWorkOrders
    },
    ProcessWorkOrderCommentsQueryKeyPrefix
  )

  const handleStatusChange = (newStatus: ProcessWorkOrderStatus, comment?: string) => {
    const dto = mapToSingleProcessWorkOrderStatusUpdateDto(data as ProcessWorkOrderDto, newStatus)
    updateProcessWorkOrder(dto, {
      onSuccess: async (updatedWorkOrder) => {
        if (comment) {
          await addComment({value: comment, commentCategoryInstanceId: updatedWorkOrder.id})
        }
      }
    })
  }

  return (
    <DataContentWrapper
      data={data}
      showLoader={isLoading || isLoadingConfig || isLoadingEditingDeleting}
      isError={isError}
      retryFunction={refetch}
      isRefetching={isRefetching}
      noDataSettings={{
        noDataMessage: t('task.message.taskNotFound'),
        onActionClick: () => onClose()
      }}
    >
      {data && configData && (
        <ProcessWorkOrderTaskCard
          item={
            {
              ...task,
              ...(data && {
                title: data.title,
                description: data.description,
                status: data.status,
                lastUpdatedAt: data.lastUpdatedAt,
                repetitionInfo: data.repetitionInfo,
                created: data.createdAt,
                dueDate: data.dueDate,
                scheduleId: data.scheduleId
              })
            } as ProcessWorkOrderTaskCardItem
          }
          // TODO: check with business if this is alright since other task types has created and modified
          //  info based on system timezone
          timezone={configData.timezone}
          onClose={onClose}
          onEditTaskClick={onEditTaskClick}
          setIsLoadingEditingDeleting={setIsLoadingEditingDeleting}
        >
          <ProcessWorkOrderDetails
            processWorkOrderTask={data}
            plantId={data.plantId}
            timezone={configData.timezone}
            onStatusChange={handleStatusChange}
          />
          <ShiftTaskAttachmentsContainer attachments={data.attachments} plantId={data.plantId} />
          <ProcessWorkOrderCommentsContainer
            plantId={data.plantId}
            commentCategoryInstanceId={data.id}
          />
        </ProcessWorkOrderTaskCard>
      )}
    </DataContentWrapper>
  )
}
